import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import ChapterHeader from "@/components/landing/ChapterHeader";
import { GUSTI } from "@/lib/constants";
import { useLang } from "@/lib/i18n";

export default function Gusti() {
  const { t } = useLang();
  const [selected, setSelected] = useState(null);

  return (
    <section id="gusti" data-testid="gusti-section" className="relative overflow-hidden py-28 sm:py-36">
      <div className="texture-paneling absolute inset-0" aria-hidden="true" />
      <div className="absolute inset-0 bg-[#FDFBF7]/90" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <ChapterHeader number="02" title={t.gusti.title} subtitle={t.gusti.subtitle} />
        <div className="grid gap-10 md:grid-cols-3">
          {GUSTI.map((g, i) => {
            const tt = t.gusti.items[g.id];
            return (
              <motion.article
                key={g.id}
                data-testid={`taste-card-${g.id}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                className="group"
              >
                <button
                  data-testid={`gusto-zoom-${g.id}`}
                  onClick={() => setSelected({ ...g, origin: tt.origin })}
                  className="arch-frame relative block aspect-[3/4] w-full cursor-zoom-in overflow-hidden border border-bordeaux/10 shadow-[0_30px_60px_-30px_rgba(171,24,43,0.35)]"
                  aria-label={g.name}
                >
                  <img
                    src={g.image}
                    alt={g.name}
                    loading="lazy"
                    className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 ${g.pos || ""}`}
                  />
                  <span className="absolute left-5 top-5 rounded-full bg-night/70 px-3 py-1 font-mono-alt text-[10px] uppercase tracking-[0.25em] text-ivory backdrop-blur">
                    {g.number}
                  </span>
                </button>
                <h3 className="mt-7 font-display text-2xl font-semibold leading-tight text-ink sm:text-3xl">{g.name}</h3>
                <p className="mt-2 font-mono-alt text-[11px] uppercase tracking-[0.25em] text-bordeaux">{tt.origin}</p>
                <p className="mt-4 text-sm leading-relaxed text-stone-600 sm:text-base">{tt.description}</p>
                <div className="mt-5 border-t border-bordeaux/15 pt-4">
                  <p className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-stone-400">
                    {t.gusti.whyLabel}
                  </p>
                  <p className="mt-2 font-display text-lg italic leading-snug text-bordeaux-dark">{tt.why}</p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="gusto-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setSelected(null)}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-night/90 p-5 backdrop-blur-md"
          >
            <motion.figure
              initial={{ scale: 0.85, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-lg"
            >
              <img
                src={selected.image}
                alt={selected.name}
                className="arch-frame max-h-[65vh] w-auto border border-ivory/20 object-contain shadow-2xl"
              />
              <figcaption className="mt-5 text-center">
                <p className="font-display text-2xl font-semibold text-ivory sm:text-3xl">{selected.name}</p>
                <p className="mt-2 font-mono-alt text-[11px] uppercase tracking-[0.25em] text-gold">
                  {selected.origin}
                </p>
              </figcaption>
              <button
                data-testid="gusto-lightbox-close"
                onClick={() => setSelected(null)}
                className="absolute -right-3 -top-3 flex h-11 w-11 items-center justify-center rounded-full bg-bordeaux text-ivory transition-transform hover:scale-110"
                aria-label="Chiudi foto"
              >
                <X className="h-5 w-5" />
              </button>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
